// One editor for the two margin-report rate tables — client charge rates and staff cost rates.
// They were two copies of the same page (and a third and fourth copy inside Financial
// configuration), so a fix to one never reached the others. The requests and bodies sent are
// exactly what each page sent before; only the page around them changed.
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Pencil, Trash2, RefreshCw, DollarSign } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { useConfirm } from '../../components/ConfirmDialog';
import {
  PAGE_WIDE, DetailDrawer, Field, FormRow, EmptyState, ErrorState, TableSkeleton, OverflowMenu,
  money, plural, fmtDate,
} from '../../components/ui';
import { TABLE_WRAP, TABLE, THEAD_ROW, TH, TH_NUM, TD, TD_NUM, Notice, codeLabel } from './financeKit';

export const SHIFT_TYPES = ['Standard', 'Weekend', 'Public Holiday', 'Sleepover', 'Active Overnight', 'Community Access', 'Domestic Assistance', 'Transport', 'High Intensity', 'Other'];
const DAY_RATES = [
  { key: 'standard_rate', label: 'Standard' },
  { key: 'saturday_rate', label: 'Saturday' },
  { key: 'sunday_rate', label: 'Sunday' },
  { key: 'public_holiday_rate', label: 'Public holiday' },
];

// 'From 1 Jan 2020 · no end'
const validity = (from, to) => `${from ? `From ${fmtDate(String(from).slice(0, 10))}` : 'Always'} · ${to ? `to ${fmtDate(String(to).slice(0, 10))}` : 'no end'}`;

export default function RatesEditor({ cfg }) {
  const confirm = useConfirm();
  const [rates, setRates] = useState([]);
  const [people, setPeople] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [form, setForm] = useState(cfg.empty);
  const [errors, setErrors] = useState({});
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [filterId, setFilterId] = useState('');
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);

  const loadRates = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.get(cfg.endpoint);
      setRates(r.data || []);
    } catch (e) {
      setError(e.response?.data?.message || `We couldn't load ${cfg.noun} rates. Check your connection, then try again.`);
    } finally { setLoading(false); }
  }, [cfg.endpoint, cfg.noun]);

  useEffect(() => {
    api.get(cfg.peopleUrl).then(r => setPeople(r.data?.[cfg.peopleKey] || r.data || [])).catch(() => {});
    loadRates();
  }, [cfg.peopleUrl, cfg.peopleKey, loadRates]);

  function openNew() { setForm(cfg.empty); setErrors({}); setEditing(null); setShowForm(true); }
  function openEdit(rate) {
    setForm({
      [cfg.idField]: rate[cfg.idField] || '',
      shift_type: rate.shift_type,
      standard_rate: rate.standard_rate,
      saturday_rate: rate.saturday_rate,
      sunday_rate: rate.sunday_rate,
      public_holiday_rate: rate.public_holiday_rate,
      pay_type: rate.pay_type || 'hourly',
      valid_from: rate.valid_from?.slice(0, 10) || '',
      valid_to: rate.valid_to?.slice(0, 10) || '',
    });
    setErrors({});
    setEditing(rate.id);
    setShowForm(true);
  }

  const validate = () => {
    const e = {};
    if (!form.shift_type) e.shift_type = 'Choose a shift type.';
    for (const { key, label } of DAY_RATES) {
      const v = form[key];
      if (v === '' || v == null) continue;
      if (!Number.isFinite(Number(v))) e[key] = `${label} rate must be a number.`;
      else if (Number(v) < 0) e[key] = `${label} rate can't be below $0.`;
    }
    if (form.valid_from && form.valid_to && form.valid_to < form.valid_from) e.valid_to = 'The end date is before the start date.';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  async function save() {
    if (!validate()) return;
    setSaving(true);
    try {
      const body = {
        [cfg.idField]: form[cfg.idField] || null,
        shift_type: form.shift_type,
        standard_rate: parseFloat(form.standard_rate) || 0,
        saturday_rate: parseFloat(form.saturday_rate) || 0,
        sunday_rate: parseFloat(form.sunday_rate) || 0,
        public_holiday_rate: parseFloat(form.public_holiday_rate) || 0,
        pay_type: form.pay_type,
        valid_from: form.valid_from || null,
        valid_to: form.valid_to || null,
      };
      if (editing) {
        await api.put(`${cfg.endpoint}/${editing}`, body);
        toast.success('Rate updated');
      } else {
        await api.post(cfg.endpoint, body);
        toast.success('Rate created');
      }
      setShowForm(false);
      loadRates();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed to save rate'); }
    finally { setSaving(false); }
  }

  const nameOf = (id, row) => {
    if (!id) return cfg.defaultLabel;
    const p = people.find(x => x.id === id);
    return p ? `${p.first_name} ${p.last_name}` : (row?.[cfg.nameField] || id);
  };

  async function del(rate) {
    const ok = await confirm('This cannot be undone. Invoices are priced from price books and are not affected.', {
      title: `Delete the ${codeLabel(rate.shift_type)} rate for ${nameOf(rate[cfg.idField], rate)}?`,
      confirmLabel: 'Delete rate', danger: true,
    });
    if (!ok) return;
    try {
      await api.delete(`${cfg.endpoint}/${rate.id}`);
      toast.success('Rate deleted');
      loadRates();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed to delete'); }
  }

  // Sync overwrites rates someone may have typed by hand, so it asks, plainly.
  async function sync() {
    const ok = await confirm(cfg.sync.confirm, { title: cfg.sync.title, confirmLabel: 'Sync and overwrite', danger: true });
    if (!ok) return;
    setSyncing(true);
    setSyncResult(null);
    try {
      const r = await api.post(cfg.sync.url);
      setSyncResult(r.data);
      if (r.data?.capped) toast(r.data.message, { duration: 8000 });
      else toast.success(`Synced: ${r.data.created} created, ${r.data.updated} updated`);
      loadRates();
    } catch (e) { toast.error(e.response?.data?.message || 'Sync failed'); }
    finally { setSyncing(false); }
  }

  // Contractors (staff only) are paid from the flat rate card on their profile, never from these rows.
  const hidden = cfg.hideRow ? rates.filter(cfg.hideRow).length : 0;
  const visible = cfg.hideRow ? rates.filter(r => !cfg.hideRow(r)) : rates;
  const filtered = visible.filter(r => !filterId || r[cfg.idField] === filterId);
  const peopleCount = new Set(visible.filter(r => r[cfg.idField]).map(r => r[cfg.idField])).size;
  const defaultCount = visible.filter(r => !r[cfg.idField]).length;

  // Group rows by person (the server already orders them that way), defaults first.
  const groups = [];
  for (const r of filtered) {
    const key = r[cfg.idField] || '__default';
    let g = groups[groups.length - 1];
    if (!g || g.key !== key) { g = { key, name: nameOf(r[cfg.idField], r), rows: [] }; groups.push(g); }
    g.rows.push(r);
  }

  const setF = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  return (
    <div className={PAGE_WIDE}>
      {/* What these rates drive, so nobody edits them expecting an invoice to change. */}
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <p className="text-sm text-slate-600 max-w-2xl">
          {cfg.intro} <Link to={cfg.link.to} className="font-medium text-primary-700 hover:underline">{cfg.link.label}</Link>
        </p>
        <div className="flex items-center gap-2 flex-wrap">
          <button type="button" onClick={sync} disabled={syncing} className="btn-secondary">
            <RefreshCw size={14} className={syncing ? 'animate-spin' : ''} /> {syncing ? 'Syncing…' : cfg.sync.label}
          </button>
          <button type="button" onClick={openNew} className="btn-primary"><Plus size={15} /> Add rate</button>
        </div>
      </div>

      {syncResult && (
        <Notice tone="slate" title="Sync complete" onDismiss={() => setSyncResult(null)}>
          {syncResult.created} created, {syncResult.updated} updated{syncResult.skipped ? `, ${syncResult.skipped} skipped` : ''} across {plural(syncResult.processed ?? syncResult[cfg.sync.processedKey] ?? 0, cfg.noun)}.
          {syncResult.capped && ` ${syncResult.remaining} still to go: run the sync again to continue.`}
        </Notice>
      )}

      {hidden > 0 && (
        <Notice tone="amber">
          {hidden} award {hidden === 1 ? 'row' : 'rows'} belonging to contractors {hidden === 1 ? 'is' : 'are'} hidden here.
          Contractors are paid from the flat rate card on their staff profile, not from award rates.
        </Notice>
      )}

      <div className="card overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between gap-3 flex-wrap">
          <p className="text-xs text-slate-600">
            {loading ? 'Loading…' : `${plural(visible.length, 'rate')} · ${plural(peopleCount, cfg.noun)} · ${plural(defaultCount, 'default')}`}
          </p>
          <div>
            <label htmlFor="rates-filter" className="sr-only">Show rates for</label>
            <select id="rates-filter" value={filterId} onChange={e => setFilterId(e.target.value)}
              className="input-field !w-auto !py-1.5 text-[13px] min-w-[180px]">
              <option value="">{cfg.allLabel}</option>
              {people.map(p => <option key={p.id} value={p.id}>{p.first_name} {p.last_name}</option>)}
            </select>
          </div>
        </div>

        {error ? (
          <ErrorState message={error} onRetry={loadRates} />
        ) : loading ? (
          <TableSkeleton rows={6} cols={6} />
        ) : filtered.length === 0 ? (
          <EmptyState icon={DollarSign}
            title={filterId ? `No rates for this ${cfg.noun}` : `No ${cfg.noun} rates yet`}
            hint={filterId ? 'Add one, or show everyone to see the default rates.' : cfg.emptyHint}
            action={(
              <div className="flex gap-2 flex-wrap justify-center">
                <button type="button" onClick={openNew} className="btn-primary"><Plus size={14} /> Add rate</button>
                {!filterId && <button type="button" onClick={sync} disabled={syncing} className="btn-secondary"><RefreshCw size={14} /> {cfg.sync.label}</button>}
              </div>
            )} />
        ) : (
          <div className={TABLE_WRAP}>
            <table className={TABLE}>
              <thead>
                <tr className={THEAD_ROW}>
                  <th scope="col" className={TH}>Shift type</th>
                  {DAY_RATES.map(d => <th key={d.key} scope="col" className={TH_NUM}>{d.label}</th>)}
                  <th scope="col" className={TH}>Charged</th>
                  <th scope="col" className={TH}>Valid</th>
                  <th scope="col" className={TH_NUM}><span className="sr-only">Actions</span></th>
                </tr>
              </thead>
              {groups.map(g => (
                <tbody key={g.key}>
                  <tr className="bg-slate-50 border-y border-slate-200">
                    <th scope="colgroup" colSpan={8} className="px-4 py-2 text-left text-[13px] font-semibold text-slate-800">
                      {g.name}
                      {g.key === '__default' && <span className="ml-2 font-normal text-slate-500">applies when no {cfg.noun}-specific rate exists</span>}
                    </th>
                  </tr>
                  {g.rows.map(r => (
                    <tr key={r.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/60">
                      <td className={TD}><span className="font-medium text-slate-800" title={r.shift_type}>{codeLabel(r.shift_type)}</span></td>
                      {DAY_RATES.map(d => <td key={d.key} className={TD_NUM}>{money(r[d.key])}</td>)}
                      <td className={`${TD} text-slate-600`}>{r.pay_type === 'flat' ? 'Flat rate' : 'Per hour'}</td>
                      <td className={`${TD} text-xs text-slate-600 whitespace-nowrap`}>{validity(r.valid_from, r.valid_to)}</td>
                      <td className={`${TD} text-right`}>
                        <OverflowMenu label={`Actions for the ${codeLabel(r.shift_type)} rate, ${g.name}`} items={[
                          { label: 'Edit rate', icon: Pencil, onClick: () => openEdit(r) },
                          { label: 'Delete rate', icon: Trash2, danger: true, onClick: () => del(r) },
                        ]} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              ))}
            </table>
          </div>
        )}
      </div>

      <DetailDrawer open={showForm} onClose={() => setShowForm(false)} width="lg"
        title={editing ? 'Edit rate' : `New ${cfg.noun} rate`}
        subtitle={cfg.drawerHint}
        footer={(
          <>
            <button type="button" onClick={() => setShowForm(false)} className="btn-secondary">Cancel</button>
            <button type="button" onClick={save} disabled={saving} className="btn-primary">{saving ? 'Saving…' : 'Save rate'}</button>
          </>
        )}>
        <div className="space-y-4">
          <FormRow cols={2}>
            <Field label={cfg.personLabel}>
              {(id) => (
                <select id={id} value={form[cfg.idField]} onChange={setF(cfg.idField)} className="input-field">
                  <option value="">{cfg.defaultLabel}</option>
                  {people.map(p => <option key={p.id} value={p.id}>{p.first_name} {p.last_name}</option>)}
                </select>
              )}
            </Field>
            <Field label="Shift type" required error={errors.shift_type}>
              {(id) => (
                <select id={id} value={form.shift_type} onChange={setF('shift_type')} className="input-field">
                  <option value="">Select type</option>
                  {SHIFT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  {form.shift_type && !SHIFT_TYPES.includes(form.shift_type) && <option value={form.shift_type}>{codeLabel(form.shift_type)}</option>}
                </select>
              )}
            </Field>
          </FormRow>
          <Field label="Charged">
            {(id) => (
              <select id={id} value={form.pay_type} onChange={setF('pay_type')} className="input-field">
                <option value="hourly">Per hour</option>
                <option value="flat">Flat rate</option>
              </select>
            )}
          </Field>
          <FormRow cols={4}>
            {DAY_RATES.map(({ key, label }) => (
              <Field key={key} label={`${label} ($)`} error={errors[key]}>
                {(id) => (
                  <input id={id} type="number" step="0.01" min="0" value={form[key]} onChange={setF(key)}
                    className="input-field text-right tabular-nums" placeholder="0.00" />
                )}
              </Field>
            ))}
          </FormRow>
          <FormRow cols={2}>
            <Field label="Valid from">
              {(id) => <input id={id} type="date" value={form.valid_from} onChange={setF('valid_from')} className="input-field" />}
            </Field>
            <Field label="Valid to" hint="Leave empty for no end date." error={errors.valid_to}>
              {(id) => <input id={id} type="date" value={form.valid_to} onChange={setF('valid_to')} className="input-field" />}
            </Field>
          </FormRow>
        </div>
      </DetailDrawer>
    </div>
  );
}
